import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import RefreshRoundedIcon from '@material-ui/icons/RefreshRounded';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import AttachFileRoundedIcon from '@material-ui/icons/AttachFileRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import BookmarkBorderRoundedIcon from '@material-ui/icons/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@material-ui/icons/BookmarkRounded';
import InsertDriveFileRoundedIcon from '@material-ui/icons/InsertDriveFileRounded';
import { CodeBlock, dracula } from 'react-code-blocks';
import Plot from 'react-plotly.js';

import { agentAccentGradients } from '../../app/Theme';
import DataService from '../../services/DataService';
import { useAuthContext } from '../../services/AuthService';
import AgentWorkflowPanel from './AgentWorkflowPanel';
import { parseAgentContent } from './rendering';
import { isAgentWorkflowV2Enabled } from './workflow';

const STORAGE_KEY = 'neurodiffeq-agent-conversations-v2';
const MAX_PINNED_CONVERSATIONS = 3;
const agentAvatarSrc = `${process.env.PUBLIC_URL}/assets/agent.png`;

const styles = (theme) => {
    const accentGradient = theme.palette.type === 'light'
        ? agentAccentGradients.light
        : agentAccentGradients.dark;
    const lightTextShadow = theme.palette.type === 'light' ? '0 0 1px rgba(0,0,0,0.25)' : 'none';

    return ({
    root: {
        height: 'calc(100vh - 68px)',
        minHeight: 0,
        boxSizing: 'border-box',
        background: theme.palette.background.default,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'background 220ms ease',
        [theme.breakpoints.down('md')]: {
            height: 'calc(100vh - 65px)',
            padding: 0,
        },
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 60px)',
            padding: 0,
        },
    },
    container: {
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        minHeight: 0,
    },
    workspace: {
        width: '100%',
        maxWidth: 'none',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'minmax(240px, 280px) 1fr',
        gap: 0,
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0,
        transition: 'grid-template-columns 220ms ease',
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: 'minmax(260px, 300px) 1fr',
        },
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2.5),
        },
    },
    workspaceCollapsed: {
        gridTemplateColumns: 'minmax(64px, 64px) 1fr',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2.5),
        },
    },
    sidebar: {
        backgroundColor: theme.palette.background.default,
        borderRadius: 0,
        border: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%',
        transition: 'width 220ms ease, min-width 220ms ease, padding 220ms ease, border-color 220ms ease',
        [theme.breakpoints.down('sm')]: {
            order: 2,
            height: 'auto',
        },
    },
    sidebarCollapsed: {
        width: theme.spacing(8),
        minWidth: theme.spacing(8),
        padding: theme.spacing(1),
        boxSizing: 'border-box',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minWidth: '100%',
        },
    },
    collapsedRail: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        height: '100%',
        width: '100%',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
        },
    },
    collapsedAgentAvatar: {
        width: theme.spacing(4),
        height: theme.spacing(4),
        flexShrink: 0,
    },
    railSpacer: {
        flex: 1,
    },
    sidebarHeader: {
        padding: theme.spacing(1.5, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
    },
    sidebarHeaderTop: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minWidth: 0,
    },
    sidebarNewConversation: {
        minWidth: 0,
        padding: theme.spacing(0.75, 1),
        fontSize: '0.78rem',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        justifySelf: 'center',
    },
    conversationList: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(1.5, 1.5, 2.5, 1.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minHeight: 0,
    },
    sidebarTabs: {
        display: 'flex',
        gap: theme.spacing(0.5),
        padding: theme.spacing(1, 1.5, 0),
    },
    sidebarTab: {
        flex: 1,
        minWidth: 0,
        justifyContent: 'flex-start',
        padding: theme.spacing(0.75, 1),
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontSize: '0.82rem',
    },
    sidebarTabActive: {
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.primary.main,
    },
    fileList: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(1.5, 1.5, 2.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minHeight: 0,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        minWidth: 0,
        padding: theme.spacing(0.75),
        borderRadius: theme.shape.borderRadius,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    fileIcon: {
        color: theme.palette.primary.main,
        marginTop: theme.spacing(0.25),
        flexShrink: 0,
    },
    fileDetails: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
    },
    fileName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    conversationItem: {
        width: '100%',
        textAlign: 'left',
        padding: theme.spacing(0.5, 0.75),
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 160ms ease',
        justifyContent: 'space-between',
        textTransform: 'none',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        position: 'relative',
    },
    conversationItemActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
    },
    conversationTitleRow: {
        flex: 1,
        minWidth: 0,
        width: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: theme.spacing(0.75, 0),
        textAlign: 'left',
        overflow: 'hidden',
    },
    conversationTitle: {
        minWidth: 0,
        fontSize: '0.88rem',
        fontWeight: 600,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'text',
    },
    renameField: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        '& .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputBase-input': {
            textAlign: 'left',
        },
    },
    pane: {
        backgroundColor: 'transparent',
        borderRadius: 0,
        border: 0,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    messagesArea: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 0,
    },
    emptyStateCard: {
        borderRadius: theme.spacing(2),
        border: `1px dashed ${theme.palette.divider}`,
        padding: theme.spacing(3),
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : '#fefefe',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
    },
    emptyStateTitle: {
        fontWeight: 600,
    },
    messageRow: {
        display: 'flex',
        width: '100%',
    },
    agentRow: {
        justifyContent: 'flex-start',
    },
    userRow: {
        justifyContent: 'flex-end',
    },
    messageBubble: {
        maxWidth: '72%',
        padding: theme.spacing(1.75, 2.25),
        borderRadius: theme.shape.borderRadius * 1.6,
        boxShadow: 'none',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    agentBubble: {
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
    },
    userBubble: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: theme.palette.getContrastText(theme.palette.primary.main),
    },
    messageMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.75rem',
        opacity: 0.65,
    },
    attachmentsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    composerArea: {
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2.5, 3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.paper : '#fdfdff',
    },
    composerForm: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    composerField: {
        flex: '1 1 280px',
    },
    composerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    attachmentsTray: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    quickPromptRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    agentAvatar: {
        height: 40,
        width: 40,
        fontWeight: 700,
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 4px 14px ${theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.28)' : 'rgba(31, 111, 74, 0.18)'}`,
        objectFit: 'cover',
        letterSpacing: '0.05em',
    },
    gradientText: {
        background: accentGradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        display: 'inline-block',
        textShadow: lightTextShadow,
    },

    typingIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        paddingLeft: theme.spacing(1),
    },
    renameActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    pinLimitMessage: {
        bottom: theme.spacing(2),
    },
    sendButton: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.getContrastText(theme.palette.secondary.main),
        '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
        },
    },
    });
};

const quickPrompts = [
    'Draft a solver plan for a 2D heat equation with Dirichlet boundaries.',
    'Outline diagnostics for a Navier–Stokes PINN experiment.',
    'Help me create a project scaffold for Burgers’ equation.',
];

const createConversation = () => {
    const now = new Date();
    return {
        id: `conv-${now.getTime()}`,
        title: `Session ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        messages: [],
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        pinned: false,
    };
};

const sortConversations = (list) =>
    [...list].sort((a, b) => {
        const pinnedOrder = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
        if (pinnedOrder !== 0) {
            return pinnedOrder;
        }
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

const formatTimestamp = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatFileSize = (value) => {
    const size = Number(value);
    if (!Number.isFinite(size) || size < 0) {
        return 'Size unavailable';
    }
    if (size < 1024) {
        return `${size} B`;
    }
    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const NeuroDiffEqAgent = ({ classes }) => {
    const auth = useAuthContext();
    const isAuthenticated = !!auth?.state?.isAuthenticated;
    const storageKey = useMemo(() => {
        const username = auth?.state?.username;
        return username ? `${STORAGE_KEY}:${username}` : STORAGE_KEY;
    }, [auth?.state?.username]);

    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(() => (
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 959.95px)').matches
    ));
    const [renameTargetId, setRenameTargetId] = useState(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isResponding, setIsResponding] = useState(false);
    const [pinLimitMessage, setPinLimitMessage] = useState('');
    const [activeSidebarTab, setActiveSidebarTab] = useState('conversations');
    const [libraryFiles, setLibraryFiles] = useState([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [filesError, setFilesError] = useState('');

    const timelineRef = useRef(null);
    const fileInputRef = useRef(null);
    const pendingRequestRef = useRef(null);

    const refreshLibraryFiles = useCallback(async () => {
        if (!isAuthenticated) {
            setLibraryFiles([]);
            return [];
        }
        setIsLoadingFiles(true);
        setFilesError('');
        try {
            const response = await DataService.ListAgentFiles();
            const files = Array.isArray(response?.data?.files) ? response.data.files : [];
            setLibraryFiles(files);
            return files;
        } catch (requestError) {
            setFilesError(requestError?.response?.data?.detail || 'Unable to load your files.');
            return [];
        } finally {
            setIsLoadingFiles(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        refreshLibraryFiles();
    }, [refreshLibraryFiles]);

    const setConversationsWithPersist = useCallback((updater) => {
        setConversations((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (typeof window !== 'undefined') {
                try {
                    window.localStorage.setItem(storageKey, JSON.stringify(next));
                } catch (error) {
                    console.warn('Unable to persist NeuroDiffEq agent conversations', error);
                }
            }
            return next;
        });
    }, [storageKey]);

    useEffect(() => {
        let stored = [];
        if (typeof window !== 'undefined') {
            try {
                const raw = window.localStorage.getItem(storageKey);
                if (raw) {
                    stored = JSON.parse(raw);
                }
            } catch (error) {
                console.warn('Unable to parse stored NeuroDiffEq conversations', error);
            }
        }

        if (!stored || !stored.length) {
            const initial = createConversation();
            stored = [initial];
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(storageKey, JSON.stringify(stored));
            }
        }

        const ordered = sortConversations(stored);
        setConversations(ordered);
        setActiveConversationId(ordered[0]?.id || null);
    }, [storageKey]);

    useEffect(() => {
        if (timelineRef.current) {
            timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
        }
    }, [activeConversationId, conversations]);

    const activeConversation = useMemo(
        () => conversations.find((conversation) => conversation.id === activeConversationId) || conversations[0] || null,
        [conversations, activeConversationId]
    );

    const messages = activeConversation?.messages || [];
    const workflowV2Enabled = isAgentWorkflowV2Enabled();

    const ensureConversationSelected = () => {
        if (activeConversation) {
            return activeConversation.id;
        }

        const nextConversation = createConversation();
        setConversationsWithPersist(sortConversations([nextConversation, ...conversations]));
        setActiveConversationId(nextConversation.id);
        return nextConversation.id;
    };

    const addMessageToConversation = useCallback((conversationId, message) => {
        setConversationsWithPersist((prev) => {
            const next = prev.map((conversation) => {
                if (conversation.id !== conversationId) {
                    return conversation;
                }

                const existingMessages = conversation.messages || [];
                const nextMessages = [...existingMessages, message];
                let nextTitle = conversation.title;

                if (
                    message.role === 'user' &&
                    !existingMessages.some((item) => item.role === 'user') &&
                    message.content
                ) {
                    const summary = message.content.trim();
                    nextTitle = summary.length > 48 ? `${summary.slice(0, 48)}…` : summary;
                }

                return {
                    ...conversation,
                    title: nextTitle,
                    messages: nextMessages,
                    updatedAt: new Date().toISOString(),
                };
            });

            return sortConversations(next);
        });

        setActiveConversationId(conversationId);
    }, [setConversationsWithPersist]);

    const replaceConversationMessages = useCallback((conversationId, messagesUpdater) => {
        setConversationsWithPersist((prev) => {
            const next = prev.map((conversation) => {
                if (conversation.id !== conversationId) {
                    return conversation;
                }

                const updatedMessages = messagesUpdater(conversation.messages || []);

                return {
                    ...conversation,
                    messages: updatedMessages,
                    updatedAt: new Date().toISOString(),
                };
            });

            return sortConversations(next);
        });
    }, [setConversationsWithPersist]);

    const handleCreateConversation = () => {
        const conversation = createConversation();
        setConversationsWithPersist((prev) => sortConversations([conversation, ...prev]));
        setActiveConversationId(conversation.id);
        setInputValue('');
        setAttachments([]);
        pendingRequestRef.current = null;
        setIsResponding(false);
    };

    const handleSelectConversation = (conversationId) => {
        if (isResponding) {
            handleStopResponse();
        }

        setActiveConversationId(conversationId);
        setInputValue('');
        setAttachments([]);
        setRenameTargetId(null);
    };

    const handleClearConversation = () => {
        const conversationId = activeConversation?.id;
        if (!conversationId) {
            return;
        }
        setConversationsWithPersist((prev) => {
            const next = prev.map((conversation) => {
                if (conversation.id !== conversationId) {
                    return conversation;
                }
                const now = new Date();
                return {
                    ...conversation,
                    title: `Session ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
                    messages: [],
                    updatedAt: now.toISOString(),
                };
            });
            return sortConversations(next);
        });
        setInputValue('');
        setAttachments([]);
    };

    const handleAttachmentChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) {
            return;
        }

        setAttachments((prev) => [
            ...prev,
            ...files.map((file) => ({
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
                file,
            })),
        ]);

        if (event.target) {
            event.target.value = '';
        }
    };

    const handleRemoveAttachment = (attachmentId) => {
        setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
    };

    const handleStopResponse = () => {
        if (pendingRequestRef.current) {
            pendingRequestRef.current.cancelled = true;
        }
        setIsResponding(false);
    };

    const handleDeleteConversation = useCallback((conversationId) => {
        if (!conversationId) {
            return;
        }

        setConversationsWithPersist((prev) => {
            const remaining = prev.filter((conversation) => conversation.id !== conversationId);
            let nextList = remaining;

            if (!nextList.length) {
                nextList = [createConversation()];
            }

            const ordered = sortConversations(nextList);
            const fallbackId = ordered[0]?.id || null;

            setActiveConversationId((current) => {
                if (current === conversationId) {
                    return fallbackId;
                }
                if (!ordered.some((conversation) => conversation.id === current)) {
                    return fallbackId;
                }
                return current;
            });

            return ordered;
        });

        setRenameTargetId((current) => (current === conversationId ? null : current));
        setRenameDraft('');

        if (activeConversationId === conversationId) {
            handleStopResponse();
            pendingRequestRef.current = null;
            setInputValue('');
            setAttachments([]);
        }
    }, [activeConversationId, handleStopResponse, setConversationsWithPersist]);

    const handleTogglePin = useCallback((conversationId) => {
        const conversation = conversations.find((item) => item.id === conversationId);
        if (!conversation) {
            return;
        }

        if (!conversation.pinned && conversations.filter((item) => item.pinned).length >= MAX_PINNED_CONVERSATIONS) {
            setPinLimitMessage(`You can pin up to ${MAX_PINNED_CONVERSATIONS} conversations.`);
            return;
        }

        setConversationsWithPersist((prev) => sortConversations(prev.map((item) => (
            item.id === conversationId
                ? { ...item, pinned: !item.pinned }
                : item
        ))));
    }, [conversations, setConversationsWithPersist]);

    const submitPrompt = async (rawText) => {
        const trimmed = rawText.trim();
        if (!trimmed) {
            return;
        }

        if (!isAuthenticated) {
            return;
        }

        const conversationId = ensureConversationSelected();
        if (!conversationId || isResponding) {
            return;
        }

        const attachmentMetadata = attachments.map(({ file, id }) => ({
            id,
            name: file.name,
            size: file.size,
            type: file.type,
        }));

        const userMessage = {
            role: 'user',
            content: trimmed,
            attachments: attachmentMetadata,
            timestamp: new Date().toISOString(),
        };

        addMessageToConversation(conversationId, userMessage);
        setInputValue('');
        setAttachments([]);
        setIsResponding(true);

        pendingRequestRef.current = { cancelled: false, conversationId };

        try {
            const historyMessages = conversations.find((item) => item.id === conversationId)?.messages || [];
            const payload = [...historyMessages, userMessage].map(({ role, content }) => ({ role, content }));
            const response = await DataService.SendAgentMessage(payload);
            const replyText = (response?.data?.reply || '').trim();

            if (pendingRequestRef.current?.cancelled) {
                return;
            }

            if (replyText) {
                addMessageToConversation(conversationId, {
                    role: 'agent',
                    content: replyText,
                    timestamp: new Date().toISOString(),
                });
            } else {
                addMessageToConversation(conversationId, {
                    role: 'agent',
                    content: 'I was unable to generate a response. Please try again shortly.',
                    timestamp: new Date().toISOString(),
                });
            }
        } catch (error) {
            if (!pendingRequestRef.current?.cancelled) {
                const detail = error?.response?.data?.detail;
                addMessageToConversation(conversationId, {
                    role: 'agent',
                    content: detail || 'I ran into an issue reaching the PINNs Agent inside PINN Hub. Please try again shortly.',
                    timestamp: new Date().toISOString(),
                });
            }
        } finally {
            if (pendingRequestRef.current?.conversationId === conversationId) {
                pendingRequestRef.current = null;
            }
            setIsResponding(false);
        }
    };

    const handleComposerSubmit = (event) => {
        event.preventDefault();
        submitPrompt(inputValue);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submitPrompt(inputValue);
        }
    };

    const handleQuickPrompt = (prompt) => {
        submitPrompt(prompt);
    };

    const renderContent = (content) => {
        return parseAgentContent(content).map((part, index) => {
            if (part.type === 'code') {
                return (
                    <CodeBlock
                        key={index}
                        text={part.value}
                        language={part.language}
                        showLineNumbers={true}
                        theme={dracula}
                        codeBlock
                    />
                );
            } else if (part.type === 'graph') {
                return <Plot key={index} {...part.spec} />;
            }
            return part.value.split('\n').map((line, lineIndex, arr) => (
                    <React.Fragment key={`${index}-${lineIndex}`}>
                        {line}
                        {lineIndex !== arr.length - 1 && <br />}
                    </React.Fragment>
                ));
        });
    };

    const beginRename = (conversation) => {
        setRenameTargetId(conversation.id);
        setRenameDraft(conversation.title || '');
    };

    const cancelRename = () => {
        setRenameTargetId(null);
        setRenameDraft('');
    };

    const commitRename = () => {
        if (!renameTargetId) {
            return;
        }

        const trimmed = renameDraft.trim();
        if (!trimmed) {
            cancelRename();
            return;
        }

        setConversationsWithPersist((prev) => sortConversations(prev.map((conversation) => (
            conversation.id === renameTargetId
                ? { ...conversation, title: trimmed, updatedAt: new Date().toISOString() }
                : conversation
        ))));

        setRenameTargetId(null);
        setRenameDraft('');
    };

    const persistServerConversationId = useCallback((localConversationId, serverConversationId) => {
        if (!localConversationId) {
            return;
        }
        setConversationsWithPersist((prev) => prev.map((conversation) => {
            if (conversation.id !== localConversationId) {
                return conversation;
            }
            if (!serverConversationId) {
                const withoutServerId = { ...conversation };
                delete withoutServerId.serverConversationId;
                return withoutServerId;
            }
            return { ...conversation, serverConversationId };
        }));
    }, [setConversationsWithPersist]);

    if (!isAuthenticated) {
        return (
            <div className={classes.root}>
                <div className={classes.container}>
                    <Paper elevation={3} style={{ padding: 32, textAlign: 'center' }}>
                        <Typography variant="h5" gutterBottom>
                            Sign in to use the PINNs Agent
                        </Typography>
                        <Typography variant="body1" color="textSecondary" paragraph>
                            Log in to access the agent workspace and plan-based messaging limits.
                        </Typography>
                        <Button variant="contained" color="primary" href="/login">
                            Go to Login
                        </Button>
                    </Paper>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <div className={clsx(classes.workspace, isSidebarCollapsed && classes.workspaceCollapsed)}>
                    <aside className={clsx(classes.sidebar, isSidebarCollapsed && classes.sidebarCollapsed)}>
                        {isSidebarCollapsed ? (
                            <div className={classes.collapsedRail}>
                                <Avatar alt="PINNs Agent" src={agentAvatarSrc} className={classes.collapsedAgentAvatar} />
                                <Tooltip title="Expand sidebar">
                                    <span>
                                        <IconButton aria-label="Expand sidebar" onClick={() => setSidebarCollapsed(false)}>
                                            <ChevronRightRoundedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="New conversation">
                                    <span>
                                        <IconButton aria-label="New conversation" color="primary" onClick={handleCreateConversation}>
                                            <AddRoundedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Files">
                                    <span>
                                        <IconButton
                                            aria-label="Files"
                                            color={activeSidebarTab === 'files' ? 'primary' : 'default'}
                                            onClick={() => {
                                                setActiveSidebarTab('files');
                                                setSidebarCollapsed(false);
                                            }}
                                        >
                                            <InsertDriveFileRoundedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={classes.railSpacer} />
                            </div>
                        ) : (
                            <>
                                <div className={classes.sidebarHeader}>
                                    <div className={classes.sidebarHeaderTop}>
                                        <Avatar alt="PINNs Agent" src={agentAvatarSrc} className={classes.agentAvatar} />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddRoundedIcon />}
                                            onClick={handleCreateConversation}
                                            className={classes.sidebarNewConversation}
                                        >
                                            New Conversation
                                        </Button>
                                        <Tooltip title="Collapse">
                                            <IconButton size="small" onClick={() => setSidebarCollapsed(true)}>
                                                <ChevronLeftRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </div>

                                <div className={classes.sidebarTabs} role="tablist" aria-label="Agent workspace">
                                    <Button
                                        role="tab"
                                        aria-selected={activeSidebarTab === 'conversations'}
                                        className={clsx(classes.sidebarTab, activeSidebarTab === 'conversations' && classes.sidebarTabActive)}
                                        onClick={() => setActiveSidebarTab('conversations')}
                                    >
                                        Conversations
                                    </Button>
                                    <Button
                                        role="tab"
                                        aria-label="Files"
                                        aria-selected={activeSidebarTab === 'files'}
                                        startIcon={<InsertDriveFileRoundedIcon />}
                                        className={clsx(classes.sidebarTab, activeSidebarTab === 'files' && classes.sidebarTabActive)}
                                        onClick={() => setActiveSidebarTab('files')}
                                    >
                                        Files
                                    </Button>
                                </div>

                                {activeSidebarTab === 'files' ? (
                                    <div className={classes.fileList} role="tabpanel" aria-label="Files">
                                        {isLoadingFiles && <CircularProgress size={20} />}
                                        {!isLoadingFiles && filesError && (
                                            <Typography variant="caption" color="error">{filesError}</Typography>
                                        )}
                                        {!isLoadingFiles && !filesError && !libraryFiles.length && (
                                            <Typography variant="body2" color="textSecondary">
                                                Uploaded files will appear here.
                                            </Typography>
                                        )}
                                        {libraryFiles.map((file) => (
                                            <div className={classes.fileItem} key={file.id} title={file.filename}>
                                                <InsertDriveFileRoundedIcon fontSize="small" className={classes.fileIcon} />
                                                <div className={classes.fileDetails}>
                                                    <Typography variant="body2" className={classes.fileName}>
                                                        {file.filename}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {formatFileSize(file.size_bytes)}
                                                    </Typography>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                <div className={classes.conversationList}>
                                    {conversations.map((conversation) => {
                                        const isActive = conversation.id === activeConversation?.id;
                                        const isRenaming = renameTargetId === conversation.id;
                                        return (
                                            <Paper
                                                key={conversation.id}
                                                elevation={0}
                                                className={clsx(
                                                    classes.conversationItem,
                                                    isActive && classes.conversationItemActive
                                                )}
                                            >
                                                {isRenaming ? (
                                                    <>
                                                        <form
                                                            onSubmit={(event) => {
                                                                event.preventDefault();
                                                                commitRename();
                                                            }}
                                                            className={classes.renameField}
                                                        >
                                                            <TextField
                                                                value={renameDraft}
                                                                onChange={(event) => setRenameDraft(event.target.value)}
                                                                variant="outlined"
                                                                size="small"
                                                                autoFocus
                                                                fullWidth
                                                            />
                                                        </form>
                                                        <div className={classes.renameActions}>
                                                            <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={commitRename}>
                                                                <CheckRoundedIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={cancelRename}>
                                                                <CloseRoundedIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            type="button"
                                                            onClick={(event) => {
                                                                const titleElement = event.currentTarget.querySelector('p');
                                                                if (titleElement && titleElement.contains(event.target)) {
                                                                    beginRename(conversation);
                                                                    return;
                                                                }
                                                                handleSelectConversation(conversation.id);
                                                            }}
                                                            className={classes.conversationTitleRow}
                                                        >
                                                            <Typography
                                                                variant="body1"
                                                                className={classes.conversationTitle}
                                                            >
                                                                {conversation.title}
                                                            </Typography>
                                                        </Button>
                                                        <div className={classes.renameActions}>
                                                            <Tooltip title={conversation.pinned ? 'Unpin conversation' : 'Pin conversation'}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-label={conversation.pinned ? 'Unpin conversation' : 'Pin conversation'}
                                                                        color={conversation.pinned ? 'primary' : 'default'}
                                                                        onClick={() => handleTogglePin(conversation.id)}
                                                                    >
                                                                        {conversation.pinned ? <BookmarkRoundedIcon fontSize="small" /> : <BookmarkBorderRoundedIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Delete conversation">
                                                                <span>
                                                                    <IconButton size="small" onClick={() => handleDeleteConversation(conversation.id)}>
                                                                        <DeleteRoundedIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </div>
                                                    </>
                                                )}
                                            </Paper>
                                        );
                                    })}
                                </div>
                                )}
                            </>
                        )}
                    </aside>

                    <section className={classes.pane}>
                        {workflowV2Enabled ? (
                            <AgentWorkflowPanel
                                conversationId={activeConversation?.id}
                                initialServerConversationId={activeConversation?.serverConversationId}
                                onServerConversationIdChange={persistServerConversationId}
                                availableFiles={libraryFiles}
                                onFilesChanged={refreshLibraryFiles}
                            />
                        ) : (
                        <div className={classes.messagesArea} ref={timelineRef}>
                            {!messages.length && (
                                <Paper elevation={0} className={classes.emptyStateCard}>
                                    <Typography variant="h6" className={classes.emptyStateTitle}>
                                        Kick off a conversation
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Share the system, dataset, or solver objective you are working on. The agent will respond with NeuroDiffEq-specific guidance, diagnostics, and next steps.
                                    </Typography>
                                    <div className={classes.quickPromptRow}>
                                        {quickPrompts.map((prompt) => (
                                            <Chip
                                                key={`intro-${prompt}`}
                                                label={prompt}
                                                variant="outlined"
                                                clickable
                                                onClick={() => handleQuickPrompt(prompt)}
                                                disabled={isResponding}
                                            />
                                        ))}
                                    </div>
                                </Paper>
                            )}

                            {messages.map((message, index) => (
                                <div
                                    key={`${message.role}-${index}-${message.timestamp}`}
                                    className={clsx(
                                        classes.messageRow,
                                        message.role === 'user' ? classes.userRow : classes.agentRow
                                    )}
                                >
                                    <Box
                                        className={clsx(
                                            classes.messageBubble,
                                            message.role === 'user' ? classes.userBubble : classes.agentBubble
                                        )}
                                    >
                                        <Typography variant="body2" component="div">
                                            {renderContent(message.content)}
                                        </Typography>
                                        {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                            <div className={classes.attachmentsList}>
                                                {message.attachments.map((attachment) => (
                                                    <Chip
                                                        key={`${attachment.id}`}
                                                        label={attachment.name}
                                                        size="small"
                                                        variant={message.role === 'user' ? 'default' : 'outlined'}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <div className={classes.messageMeta}>
                                            <Typography variant="caption">
                                                {message.role === 'user' ? 'You' : 'Agent'} · {formatTimestamp(message.timestamp)}
                                            </Typography>
                                        </div>
                                    </Box>
                                </div>
                            ))}

                            {isResponding && (
                                <div className={clsx(classes.messageRow, classes.agentRow)}>
                                    <div className={classes.typingIndicator}>
                                        <CircularProgress size={16} thickness={5} />
                                        <span>Agent is composing a reply…</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {!workflowV2Enabled && <div className={classes.composerArea}>
                            <form className={classes.composerForm} onSubmit={handleComposerSubmit}>
                                <TextField
                                    multiline
                                    minRows={1}
                                    maxRows={6}
                                    variant="outlined"
                                    placeholder="Describe the system or ask the agent for guidance…"
                                    value={inputValue}
                                    onChange={(event) => setInputValue(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    fullWidth
                                    className={classes.composerField}
                                    disabled={isResponding}
                                />
                                <div className={classes.composerActions}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        hidden
                                        multiple
                                        onChange={handleAttachmentChange}
                                    />
                                    <Tooltip title="Attach files">
                                        <span>
                                            <IconButton onClick={() => fileInputRef.current?.click()} disabled={isResponding}>
                                                <AttachFileRoundedIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {isResponding && (
                                        <Tooltip title="Stop response">
                                            <span>
                                                <IconButton onClick={handleStopResponse}>
                                                    <StopRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Send">
                                        <span>
                                            <IconButton
                                                color="secondary"
                                                type="submit"
                                                disabled={isResponding || !inputValue.trim()}
                                                className={classes.sendButton}
                                            >
                                                <SendRoundedIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </div>
                            </form>

                            {attachments.length > 0 && (
                                <div className={classes.attachmentsTray}>
                                    {attachments.map((item) => (
                                        <Chip
                                            key={item.id}
                                            label={item.file.name}
                                            onDelete={() => handleRemoveAttachment(item.id)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>}
                    </section>
                </div>
            </div>
            <Snackbar
                open={Boolean(pinLimitMessage)}
                autoHideDuration={3500}
                onClose={() => setPinLimitMessage('')}
                message={pinLimitMessage}
                className={classes.pinLimitMessage}
            />
        </div>
    );
};

export default withStyles(styles)(NeuroDiffEqAgent);
